import React from 'react';
import { Modal } from '@mantine/core';
import useCreateBrand from '../../../hooks/brands/useCreateBrand.ts';
import useUpdateBrandImage from '../../../hooks/brands/useUpdateBrandImage.tsx';
import {
  CreateBrandRequest,
  UpdateBrandImageRequest,
} from '../../../api/requests/brand-request.ts';
import MutateBrandForm, {
  MutateBrandFormValues,
} from '../../ui/brands/MutateBrandForm.tsx';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { useMutation } from '@tanstack/react-query';
import type { ErrorResponse } from '../../../api/responses/error-response.ts';

interface AddBrandModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddBrandModal = ({ opened, onClose, onSuccess }: AddBrandModalProps) => {
  const { mutateAsync: createBrand } = useCreateBrand();
  const { mutateAsync: updateBrandImage } = useUpdateBrandImage();

  const { mutate: handleSubmit, isPending } = useMutation<
    void,
    ErrorResponse,
    MutateBrandFormValues
  >({
    mutationFn: async (formValues) => {
      const createBrandRequest: CreateBrandRequest = {
        name: formValues.name.trim(),
        services: formValues.services,
      };
      const createBrandResponse = await createBrand(createBrandRequest);
      const brandId = createBrandResponse.id;

      const updateBrandImageRequest: UpdateBrandImageRequest = {
        brandId,
        file: formValues.uploadedFiles[0],
      };
      await updateBrandImage(updateBrandImageRequest);
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      if (error.code === 'BRAND_NAME_ALREADY_EXISTS') {
        notifyError('新增品牌失敗', '品牌名稱已存在');
      } else {
        notifyError('新增品牌失敗', error.message);
      }
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="新增品牌"
      centered
      closeOnClickOutside={false}
      styles={{ title: { fontWeight: 700, fontSize: 16 } }}
    >
      <MutateBrandForm
        initialValues={{
          name: '',
          services: [],
          originalFiles: [],
        }}
        onSubmit={handleSubmit}
        onCancel={onClose}
        isSubmitting={isPending}
        submitButtonText="新增"
      />
    </Modal>
  );
};

export { AddBrandModal };
